<template>
  <v-app
    class="search-bar"
    data-search-bar
  >
    <form
      class="search-bar__container"
      :style="{ zIndex: isDialogVisible ? 5 : undefined }"
      @submit.prevent="handleSubmitForm"
    >
      <SearchInput
        ref="input"
        v-model="query"
        class="search-bar__input"
        :placeholder="placeholder"
        @click="handleClickSearchInput"
        @focus="handleFocusSearchInput"
        @submit="handleSubmitForm"
        @keydown.native.up.prevent="handleKeydownArrowUp"
        @keydown.native.down.prevent="handleKeydownArrowDown"
        @keydown.native.esc.prevent="handleKeydownEsc"
      />

      <div
        v-if="!isMobile && isDialogVisible"
        class="search-bar__dropdown-wrapper"
      >
        <div class="overflow-hidden rounded-sm">
          <v-card
            class="search-bar__dropdown"
            rounded="sm"
          >
            <SearchResults
              ref="searchResults"
              :query="query"
              :town="town"
              :is-office-ip="isOfficeIp"
              x-space-class="px-4"
              @activate-dropdown-item="handleActivateDropdownItem"
              @deactivate-dropdown-item="handleDeactivateDropdownItem"
            />
          </v-card>
        </div>
      </div>
    </form>

    <v-overlay
      v-if="!isMobile"
      v-model="isDialogVisible"
      :opacity="0"
      z-index="1"
      @click="handleClickOverlay"
    />
  </v-app>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { Location } from 'www/vue_components/common/SearchBar/types'
import buildSearchUrl from 'www/vue_components/common/SearchBar/functions/buildSearchUrl'
import SearchInput from 'www/vue_components/common/SearchBar/components/SearchInput.vue'
import SearchResults from 'www/vue_components/common/SearchBar/components/SearchResults.vue'
import sendYaGoal from 'www/vue_components/common/SearchBar/functions/sendYaGoal'

const props = defineProps<{
  placeholder: string
  town: {
    id: number
    name: string
    name_pred: string
    translit: string
  }
  searchUrl: string
  location?: Location
  isOfficeIp: boolean
}>()
const emit = defineEmits(['click-search-btn'])

const isMobile = window.MOBILE_VERSION

const input = ref<InstanceType<typeof SearchInput>>()
const searchResults = ref<InstanceType<typeof SearchResults> & {
  moveUp:() => void
  moveDown: () => void
}>()

const query = ref('')

const isDialogVisible = ref(false)

const dropdownItemLink = ref('')

function handleClickSearchInput() {
  if (isMobile) {
    isDialogVisible.value = true

    emit('click-search-btn')
  }
}

function handleFocusSearchInput() {
  if (!isMobile) {
    isDialogVisible.value = true

    emit('click-search-btn')

    if (!props.isOfficeIp) {
      sendYaGoal('search_focus')
    }
  }
}

function handleClickOverlay() {
  isDialogVisible.value = false
}

function handleKeydownArrowUp() {
  searchResults.value?.moveUp()
}

function handleKeydownArrowDown() {
  searchResults.value?.moveDown()
}

function handleKeydownEsc(event: Event) {
  isDialogVisible.value = false;
  (event.target as HTMLInputElement).blur()
}

function handleActivateDropdownItem({ title, link }: { title?: string, link?: string }) {
  query.value = title ?? ''
  dropdownItemLink.value = link ?? ''
}

function handleDeactivateDropdownItem() {
  dropdownItemLink.value = ''
}

function handleSubmitForm() {
  let redirectUrl = ''

  if (dropdownItemLink.value) {
    if (!props.isOfficeIp) {
      sendYaGoal('oldSearch_dropdownItem')
    }

    redirectUrl = dropdownItemLink.value
  } else if (query.value) {
    if (!props.isOfficeIp) {
      sendYaGoal('oldSearch_searchButton')
    }

    redirectUrl = buildSearchUrl({
      query: query.value,
      searchUrl: props.searchUrl,
      location: props.location,
    })
  }

  if (redirectUrl) {
    window.location.assign(redirectUrl)
  }
}
</script>

<style scoped lang="scss">
@import '~www/themes/doctors/common/variables';

.search-bar {
  &__container {
    position: relative;
    display: flex;
    border-radius: $border-radius-sm;
    background-color: $ui-kit-bg-gray-0;
  }

  &__input {
    flex-grow: 1;
  }

  &__dropdown-wrapper {
    position: absolute;
    left: 0;
    right: 0;
    top: 56px;
    border-radius: $border-radius-sm;

    @include elevation-2;
  }

  &__dropdown {
    overflow: auto;
    max-height: 480px;
  }
}
</style>

<style lang="scss">
@import '~www/themes/doctors/common/mixins';

#app[data-search-bar] {
  @include v-app-reset;
}
</style>
